import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen, CheckCircle2, Download, Eye, FilePlus2, History, Pencil, RefreshCcw, Rocket, RotateCcw, Route, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { ManualReader, Markdown } from "../../components/manual/ManualReader";
import { AppFlowViewer } from "../../components/appflow/AppFlowViewer";   // Phase 222
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, downloadBlob } from "../../lib/api";

const fmtDateTime = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
};
const SOURCE_META = {
  release: { label: "Release", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  manual: { label: "Published", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  edit: { label: "Edit", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};
const SourceBadge = ({ source }) => {
  const m = SOURCE_META[source] || { label: source, cls: "" };
  return <Badge variant="outline" className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
};

export default function UserManual() {
  const [current, setCurrent] = useState(null);     // {version, bundled_release}
  const [versions, setVersions] = useState(null);
  const [viewVersionId, setViewVersionId] = useState(null);
  const [viewVersion, setViewVersion] = useState(null);
  const [tab, setTab] = useState("read");
  const [busy, setBusy] = useState(false);
  // dialogs
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({ label: "", release_notes: "" });
  const [editor, setEditor] = useState(null);      // {guide, chapter|null, title, content_md, notes}
  const [editorPreview, setEditorPreview] = useState(false);
  const [confirm, setConfirm] = useState(null);    // {type:'delete-version'|'make-current'|'delete-chapter', ...}

  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([api.get("/platform/manual/current"), api.get("/platform/manual/versions")]);
      setCurrent(c.data);
      setVersions(v.data);
      setViewVersionId((id) => (id && v.data.some((x) => x.id === id) ? id : c.data.version.id));
    } catch (e) { toast.error(errMsg(e)); setVersions([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // load a non-current version when picked
  useEffect(() => {
    if (!viewVersionId || !current) return;
    if (viewVersionId === current.version.id) { setViewVersion(current.version); return; }
    setViewVersion(null);
    api.get(`/platform/manual/versions/${viewVersionId}`).then((r) => setViewVersion(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [viewVersionId, current]);

  const isViewingCurrent = viewVersion && current && viewVersion.id === current.version.id;

  const publish = async () => {
    if (!publishForm.release_notes.trim()) return toast.error("Enter release notes for this version");
    setBusy(true);
    try {
      const r = await api.post("/platform/manual/publish", publishForm);
      toast.success(`Version v${r.data.number} published`);
      setPublishOpen(false); setPublishForm({ label: "", release_notes: "" });
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const syncRelease = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/manual/sync-release");
      toast.success(r.data.synced ? `Release ${r.data.release_tag} published as v${r.data.number}` : `Already in sync (${r.data.release_tag} → v${r.data.number})`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openEditor = (guide, chapter) => setEditor({
    guide, chapter, title: chapter ? chapter.title : "", content_md: chapter ? chapter.content_md : "", notes: "",
  });

  const saveChapter = async () => {
    if (!editor.title.trim()) return toast.error("Enter a chapter title");
    if (!editor.content_md.trim()) return toast.error("Chapter content cannot be empty");
    setBusy(true);
    try {
      const r = await api.put("/platform/manual/chapters", {
        guide_key: editor.guide.key, chapter_key: editor.chapter?.key || null,
        title: editor.title, content_md: editor.content_md, notes: editor.notes || null,
      });
      toast.success(`Saved as new version v${r.data.version.number}`);
      setEditor(null); setEditorPreview(false);
      setViewVersionId(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const runConfirm = async () => {
    setBusy(true);
    try {
      if (confirm.type === "delete-version") {
        await api.delete(`/platform/manual/versions/${confirm.version.id}`);
        toast.success(`Version v${confirm.version.number} deleted`);
      } else if (confirm.type === "make-current") {
        await api.put(`/platform/manual/versions/${confirm.version.id}/make-current`);
        toast.success(`Version v${confirm.version.number} is now the current manual`);
        setViewVersionId(confirm.version.id);
      } else if (confirm.type === "delete-chapter") {
        const r = await api.delete(`/platform/manual/chapters/${confirm.guide.key}/${confirm.chapter.key}`);
        toast.success(`Chapter removed — saved as v${r.data.version.number}`);
        setViewVersionId(null);
      }
      setConfirm(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const exportVersion = async (v, format) => {
    try {
      await downloadBlob(`/platform/manual/export?format=${format}&version_id=${v.id}`, `MunicipalConnect-Manual-v${v.number}-Complete.${format}`);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const chapterActions = useCallback((g, c) => (
    <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEditor(g, c)} data-testid={`manual-edit-${g.key}-${c.key}`}>
        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
      </Button>
      {c.custom && (
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive hover:text-destructive"
          onClick={() => setConfirm({ type: "delete-chapter", guide: g, chapter: c })} data-testid={`manual-delete-${g.key}-${c.key}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  ), []);

  const versionPicker = useMemo(() => versions && (
    <Select value={viewVersionId || ""} onValueChange={setViewVersionId}>
      <SelectTrigger className="w-64 h-9" data-testid="manual-version-select"><SelectValue placeholder="Version" /></SelectTrigger>
      <SelectContent>
        {versions.map((v) => (
          <SelectItem key={v.id} value={v.id} data-testid={`manual-version-option-${v.number}`}>
            v{v.number} · {v.label}{v.is_current ? " (current)" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ), [versions, viewVersionId]);

  if (!current || !versions) return <Spinner label="Loading user manual..." />;
  const cur = current.version;
  const rel = current.bundled_release || {};

  return (
    <div className="space-y-5">
      <PageHeader title="User Manual" subtitle="Role-based documentation for every console — versioned, searchable and exportable">
        <Badge variant="outline" className="text-xs" data-testid="manual-current-badge">Current: v{cur.number} · {cur.release_tag}</Badge>
        <Button variant="outline" size="sm" onClick={syncRelease} disabled={busy} data-testid="manual-sync-release-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> {rel.in_sync ? "Release in sync" : "Sync release"}
        </Button>
        <Button size="sm" onClick={() => setPublishOpen(true)} disabled={busy} data-testid="manual-publish-button">
          <Rocket className="h-4 w-4 mr-1.5" /> Publish new version
        </Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="read" data-testid="manual-tab-read"><BookOpen className="h-4 w-4 mr-1.5" /> Read</TabsTrigger>
          <TabsTrigger value="manage" data-testid="manual-tab-manage"><History className="h-4 w-4 mr-1.5" /> Manage versions</TabsTrigger>
          <TabsTrigger value="appflow" data-testid="help-tab-appflow"><Route className="h-4 w-4 mr-1.5" /> App Flow</TabsTrigger>
        </TabsList>

        {/* ---------------- APP FLOW (Phase 222: Super Admin's own guide only) ---------------- */}
        <TabsContent value="appflow" className="mt-4">
          <AppFlowViewer fetchPath="/platform/app-flow" exportPath="/platform/app-flow/export.pptx" />
        </TabsContent>

        {/* ---------------- READ ---------------- */}
        <TabsContent value="read" className="mt-4 space-y-3">
          {viewVersion && !isViewingCurrent && (
            <div className="text-xs bg-amber-50 text-amber-900 border border-amber-200 rounded-md px-3 py-2 flex items-center justify-between gap-3" data-testid="manual-old-version-banner">
              <span>You are viewing <b>v{viewVersion.number} · {viewVersion.label}</b> — not the current manual. Editing is available on the current version only.</span>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setConfirm({ type: "make-current", version: viewVersion })} data-testid="manual-banner-make-current">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Make current
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setViewVersionId(cur.id)} data-testid="manual-banner-back-current">Back to current</Button>
              </div>
            </div>
          )}
          {!viewVersion ? <Spinner label="Loading version..." /> : (
            <ManualReader
              key={viewVersion.id}
              guides={viewVersion.guides}
              versionMeta={viewVersion}
              searchPath={`/platform/manual/search?version_id=${viewVersion.id}`}
              exportPath={`/platform/manual/export?version_id=${viewVersion.id}`}
              toolbarExtra={
                <>
                  {versionPicker}
                  {isViewingCurrent && (
                    <Select onValueChange={(gk) => openEditor(viewVersion.guides.find((g) => g.key === gk), null)} value="">
                      <SelectTrigger className="w-44 h-9" data-testid="manual-add-chapter-select">
                        <span className="flex items-center text-sm"><FilePlus2 className="h-4 w-4 mr-1.5" /> Add chapter to…</span>
                      </SelectTrigger>
                      <SelectContent>
                        {viewVersion.guides.map((g) => <SelectItem key={g.key} value={g.key} data-testid={`manual-add-chapter-${g.key}`}>{g.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </>
              }
              onChapterAction={isViewingCurrent ? chapterActions : undefined}
            />
          )}
        </TabsContent>

        {/* ---------------- MANAGE ---------------- */}
        <TabsContent value="manage" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Current version</p>
              <p className="text-xl font-semibold font-heading" data-testid="manage-current-number">v{cur.number}</p>
              <p className="text-xs text-muted-foreground truncate">{cur.label} · {fmtDateTime(cur.created_at)}</p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Deployed release (bundled docs)</p>
              <p className="text-xl font-semibold font-heading" data-testid="manage-release-tag">{rel.release_tag || "—"}</p>
              <p className={`text-xs ${rel.in_sync ? "text-emerald-700" : "text-amber-700"}`} data-testid="manage-release-sync">
                {rel.in_sync ? "Published — manual matches this deployment" : "Not yet published — click Sync release"}
              </p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Versions kept</p>
              <p className="text-xl font-semibold font-heading" data-testid="manage-version-count">{versions.length}</p>
              <p className="text-xs text-muted-foreground">Every edit, publish and release is an immutable snapshot</p>
            </CardContent></Card>
          </div>

          {rel.notes && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Release notes — {rel.release_tag}</CardTitle></CardHeader>
              <CardContent className="pt-0"><Markdown>{rel.notes}</Markdown></CardContent>
            </Card>
          )}

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Version history</CardTitle></CardHeader>
            <CardContent className="p-0 border-t">
              <div className="overflow-x-auto">
                <Table data-testid="manual-versions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Label / notes</TableHead>
                      <TableHead>Release</TableHead>
                      <TableHead className="text-center">Chapters</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versions.map((v) => (
                      <TableRow key={v.id} data-testid={`manual-version-row-${v.number}`} className={v.is_current ? "bg-primary/[0.04]" : ""}>
                        <TableCell className="font-medium whitespace-nowrap">
                          v{v.number}
                          {v.is_current && <Badge className="ml-2 text-[10px]" data-testid="manual-version-current-badge">Current</Badge>}
                        </TableCell>
                        <TableCell><SourceBadge source={v.source} /></TableCell>
                        <TableCell className="max-w-[360px]">
                          <p className="text-sm truncate" title={v.label}>{v.label}</p>
                          {v.release_notes && <p className="text-xs text-muted-foreground line-clamp-2" title={v.release_notes}>{v.release_notes}</p>}
                          {v.carried_custom_chapters > 0 && <p className="text-[11px] text-muted-foreground">Carried forward {v.carried_custom_chapters} custom chapter{v.carried_custom_chapters === 1 ? "" : "s"}</p>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{v.release_tag || "-"}</TableCell>
                        <TableCell className="text-center text-sm tabular-nums">{v.chapter_count}{v.custom_chapter_count ? <span className="text-xs text-muted-foreground"> (+{v.custom_chapter_count} custom)</span> : null}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {fmtDateTime(v.created_at)}
                          <p className="text-muted-foreground">{v.created_by_name || "—"}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setViewVersionId(v.id); setTab("read"); }} data-testid={`manual-version-view-${v.number}`}>
                              <Eye className="h-3.5 w-3.5 mr-1" /> View
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => exportVersion(v, "pdf")} data-testid={`manual-version-pdf-${v.number}`}>
                              <Download className="h-3.5 w-3.5 mr-1" /> PDF
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => exportVersion(v, "md")} data-testid={`manual-version-md-${v.number}`}>
                              <Download className="h-3.5 w-3.5 mr-1" /> MD
                            </Button>
                            {!v.is_current && (
                              <>
                                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setConfirm({ type: "make-current", version: v })} data-testid={`manual-version-make-current-${v.number}`}>
                                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Make current
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive hover:text-destructive" onClick={() => setConfirm({ type: "delete-version", version: v })} data-testid={`manual-version-delete-${v.number}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground text-sm flex items-center gap-1.5"><Upload className="h-4 w-4" /> How releases update the manual</p>
              <p>Every deployment ships an updated manual inside the application. On startup the platform compares the bundled release with the published versions; if it changed, a new <b>Release</b> version is published automatically and becomes current. Chapters you added yourself are carried forward; release chapters you edited are replaced by the new release text (your text stays available in the previous version).</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------- publish dialog ---------- */}
      <Dialog open={publishOpen} onOpenChange={(o) => !o && setPublishOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="manual-publish-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Rocket className="h-4 w-4" /> Publish new manual version</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Creates an immutable snapshot of the current content as <b>v{versions.length ? Math.max(...versions.map((v) => v.number)) + 1 : 1}</b> and makes it current.</p>
            <div className="space-y-1">
              <Label>Label (optional)</Label>
              <Input value={publishForm.label} onChange={(e) => setPublishForm({ ...publishForm, label: e.target.value })} placeholder="e.g. Q3 documentation review" data-testid="manual-publish-label" />
            </div>
            <div className="space-y-1">
              <Label>Release notes *</Label>
              <Textarea rows={5} value={publishForm.release_notes} onChange={(e) => setPublishForm({ ...publishForm, release_notes: e.target.value })}
                placeholder="What changed in this version? (markdown supported)" data-testid="manual-publish-notes" />
            </div>
            <Button className="w-full" onClick={publish} disabled={busy || !publishForm.release_notes.trim()} data-testid="manual-publish-submit">
              {busy ? "Publishing..." : "Publish version"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- chapter editor ---------- */}
      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) { setEditor(null); setEditorPreview(false); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="manual-editor-dialog">
          {editor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {editor.chapter ? <Pencil className="h-4 w-4" /> : <FilePlus2 className="h-4 w-4" />}
                  {editor.chapter ? "Edit chapter" : "Add chapter"} — {editor.guide.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Chapter title *</Label>
                  <Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="e.g. Local SOP: Cash handover" data-testid="manual-editor-title" />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Content (Markdown) *</Label>
                  <div className="flex gap-1">
                    <Button variant={editorPreview ? "ghost" : "secondary"} size="sm" className="h-7 text-xs" onClick={() => setEditorPreview(false)} data-testid="manual-editor-write-tab">Write</Button>
                    <Button variant={editorPreview ? "secondary" : "ghost"} size="sm" className="h-7 text-xs" onClick={() => setEditorPreview(true)} data-testid="manual-editor-preview-tab">Preview</Button>
                  </div>
                </div>
                {editorPreview ? (
                  <div className="border rounded-md p-4 min-h-[320px] bg-card" data-testid="manual-editor-preview">
                    <Markdown>{editor.content_md || "*Nothing to preview yet.*"}</Markdown>
                  </div>
                ) : (
                  <Textarea rows={16} value={editor.content_md} onChange={(e) => setEditor({ ...editor, content_md: e.target.value })}
                    className="font-mono text-xs leading-relaxed" placeholder={"Use **bold**, lists, tables and ### sub-headings…"} data-testid="manual-editor-content" />
                )}
                <div className="space-y-1">
                  <Label>Change note (optional)</Label>
                  <Input value={editor.notes} onChange={(e) => setEditor({ ...editor, notes: e.target.value })} placeholder="Why this change? Shown in version history" data-testid="manual-editor-notes" />
                </div>
                <div className="flex items-center justify-between gap-3 pt-1">
                  <p className="text-[11px] text-muted-foreground">Saving creates a new version — the previous text stays in history.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditor(null); setEditorPreview(false); }} data-testid="manual-editor-cancel">Cancel</Button>
                    <Button onClick={saveChapter} disabled={busy} data-testid="manual-editor-save">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : "Save as new version"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- confirmations ---------- */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="manual-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "delete-version" && `Delete version v${confirm.version.number}?`}
              {confirm?.type === "make-current" && `Make v${confirm.version.number} the current manual?`}
              {confirm?.type === "delete-chapter" && `Remove chapter “${confirm.chapter.title}”?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "delete-version" && "This snapshot is permanently removed from history. The current version is not affected."}
              {confirm?.type === "make-current" && "Staff will immediately see this version in their Help screens. Newer versions stay in history and can be restored the same way."}
              {confirm?.type === "delete-chapter" && "The chapter is removed and a new version is saved. The text remains in the previous version."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} disabled={busy}
              className={confirm?.type !== "make-current" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              data-testid="manual-confirm-action">
              {busy ? "Working..." : confirm?.type === "make-current" ? "Make current" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
